import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { NotificationsProvider } from "@/lib/notifications";
import { AppShell } from "@/components/AppShell";
import { AdminShell } from "@/components/AdminShell";
import { Toaster } from "@/components/ui/sonner";
import { getToken, getAdminToken, saveAdminToken, apiFetch, adminFetch } from "@/lib/api";

import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import { LegalPage } from "@/pages/LegalPage";
import HomePage from "@/pages/HomePage";
import TransportsPage from "@/pages/TransportsPage";
import MissionsPage from "@/pages/MissionsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import ProfilePage from "@/pages/ProfilePage";
import TripsPage from "@/pages/TripsPage";
import TripDetailPage from "@/pages/TripDetailPage";
import PublishTripPage from "@/pages/PublishTripPage";
import FreeVehiclesPage from "@/pages/FreeVehiclesPage";
import CarrierRoutesPage from "@/pages/CarrierRoutesPage";
import FleetPage from "@/pages/FleetPage";
import DriversPage from "@/pages/DriversPage";
import CarriersPage from "@/pages/CarriersPage";
import OffersHistoryPage from "@/pages/OffersHistoryPage";
import ChatsPage from "@/pages/ChatsPage";
import ChatDetailPage from "@/pages/ChatDetailPage";
import ServicesPage from "@/pages/ServicesPage";
import ServiceCategoryPage from "@/pages/ServiceCategoryPage";
import ServiceRequestsPage from "@/pages/ServiceRequestsPage";
import PreferencesPage from "@/pages/PreferencesPage";
import DepotStatsPage from "@/pages/DepotStatsPage";
import FeedbackPage from "@/pages/FeedbackPage";
import DispatchPage from "@/pages/DispatchPage";
import AdminPage from "@/pages/AdminPage";
import AdminLoginPage from "@/pages/AdminLoginPage";

function FullScreenLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  // Admin separato dai ruoli operativi: un admin autenticato (non in modalità "Visualizza come")
  // non vede mai la Home operativa, viene portato al pannello amministrativo.
  if (user.is_admin && !user.is_impersonated) return <Navigate to="/admin" replace />;
  return (
    <NotificationsProvider>
      <AppShell>{children}</AppShell>
    </NotificationsProvider>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// Admin area is authenticated independently from normal users, via the admin
// token. A logged-in NON-admin is sent to their own area ("/"); an anonymous
// visitor is sent to the dedicated /admin/login.
function RequireAdmin({ children }) {
  const [state, setState] = useState("checking"); // checking | ok | user | anon
  useEffect(() => {
    let alive = true;
    (async () => {
      if (getAdminToken()) {
        try {
          const me = await adminFetch("/auth/me");
          if (me.is_admin) {
            if (alive) setState("ok");
            return;
          }
        } catch {
          /* fall through */
        }
      }
      if (getToken()) {
        try {
          const me = await apiFetch("/auth/me");
          if (me.is_admin) {
            saveAdminToken(getToken());
            if (alive) setState("ok");
          } else if (alive) {
            setState("user");
          }
          return;
        } catch {
          /* fall through */
        }
      }
      if (alive) setState("anon");
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (state === "checking") return <FullScreenLoader />;
  if (state === "user") return <Navigate to="/" replace />;
  if (state === "anon") return <Navigate to="/admin/login" replace />;
  return <AdminShell>{children}</AdminShell>;
}

const PROTECTED = [
  ["/", HomePage],
  ["/transports", TransportsPage],
  ["/missions", MissionsPage],
  ["/notifications", NotificationsPage],
  ["/profile", ProfilePage],
  ["/trips", TripsPage],
  ["/trip/:id", TripDetailPage],
  ["/publish-trip", PublishTripPage],
  ["/free-vehicles", FreeVehiclesPage],
  ["/carrier-routes", CarrierRoutesPage],
  ["/fleet", FleetPage],
  ["/drivers", DriversPage],
  ["/carriers", CarriersPage],
  ["/offers-history", OffersHistoryPage],
  ["/chats", ChatsPage],
  ["/chat/:id", ChatDetailPage],
  ["/services", ServicesPage],
  ["/services/:category", ServiceCategoryPage],
  ["/service-requests", ServiceRequestsPage],
  ["/preferences", PreferencesPage],
  ["/depot-stats", DepotStatsPage],
  ["/feedback", FeedbackPage],
  ["/ripartizione", DispatchPage],
];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Admin area — kept fully separate from normal user routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<RequireAdmin><AdminPage /></RequireAdmin>} />
          <Route path="/admin/*" element={<RequireAdmin><AdminPage /></RequireAdmin>} />

          {/* Public */}
          {/* /login è SEMPRE accessibile e non fa redirect automatico (il redirect avviene solo dopo un login riuscito). */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/terms" element={<LegalPage kind="terms" />} />

          {/* Authenticated user app */}
          {PROTECTED.map(([path, Component]) => (
            <Route key={path} path={path} element={<RequireAuth><Component /></RequireAuth>} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </AuthProvider>
  );
}

export default App;
